import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Loader2, Zap } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useExamenes } from '../../hooks/useExamenes';
import { usePacientes } from '../../hooks/usePacientes';
import { createFactura, updateFactura } from '../../services/facturasService';
import { fetchCoberturaSeguro } from '../../services/segurosService';
import { formatCurrency } from '../../utils/formatters';
// EEM-1/CLI-3: electronic mode follows the `facturacion-electronica` page
// (fe-config single source of truth, same list the backend gates check); the
// stored-Cliente select (CLI-3) is fed by the DGII client list endpoint.
import { obtenerClientesDGII, ClienteDGII } from '../../services/dgiiService';
import { usePagePermissions } from '../../hooks/usePagePermissions';
// CLI-2: the stored-Cliente select must mirror the same DGII checksum the
// backend enforces on save — an invalid identifier can never leave the form
// (the e31 emission prevalidar is fail-closed anyway, R2-6).
import { validarRNC } from '../../utils/rnc';

interface Paciente {
  _id: string;
  nombre: string;
  codigo: string;
  seguro?: {
    _id: string;
    nombre: string;
    descuento: number;
  } | null;
}

interface Examen {
  _id: string;
  nombre: string;
  codigo: string;
  precio: number;
}

interface ExamenSeleccionado {
  examenId: string;
  cantidad: number;
  precioUnitario: number;
  // EEM-7: per-item ITBIS flag (default false = EXENTO), same default as the
  // backend's calcularSubtotales (!!examen.aplicaImpuesto).
  aplicaImpuesto?: boolean;
}

interface FacturaFormProps {
  onClose: () => void;
  onSuccess?: () => void;
  paciente?: Paciente | null;
  mode?: 'creacion' | 'edicion';
  facturaInicial?: any;
  /**
   * Factura a aseguradora (desde el resumen por aseguradora del reporte): no
   * pide paciente ni genera muestras. Los exámenes de la póliza se precargan
   * con el descuento de la ARS ya aplicado y el comprobante es crédito fiscal.
   */
  seguroFactura?: { id: string; nombre: string } | null;
  /**
   * Líneas que esa ARS consumió en el período del reporte (examen + cantidad
   * realizada). Se cruzan con la póliza para tomar precio y descuento. Sin
   * esto, se precarga la póliza completa con cantidad 1.
   */
  examenesFactura?: { examenId: string; nombre?: string; cantidad: number }[] | null;
}

type TipoFactura = 'consumidor_final' | 'credito_fiscal' | 'gubernamental' | 'secuencia_automatica';

/**
 * Interpreta un precio tecleado sin romper el formato local (RD): la coma es
 * separador de miles y el punto es decimal. Acepta "1,250.50", "1,250",
 * "12,50" y "1250.50". Con `permitirParcial` también admite un separador
 * final ("12.") mientras el usuario todavía está escribiendo.
 *
 * Devuelve null cuando el texto no es un importe interpretable, para que el
 * llamador deje el estado intacto en lugar de guardar un número inventado.
 */
const parsePrecio = (texto: string, permitirParcial = false): number | null => {
  let t = texto.trim();
  if (t === '') return null;
  if (permitirParcial) t = t.replace(/[.,]$/, '');
  if (t === '') return null;

  // Coma de miles: "1,250" / "1,250.50"
  if (/^\d{1,3}(,\d{3})+(\.\d{1,2})?$/.test(t)) {
    return Number(t.replace(/,/g, ''));
  }
  // Coma decimal: "12,50"
  if (/^\d+,\d{1,2}$/.test(t)) {
    return Number(t.replace(',', '.'));
  }
  // Punto decimal: "1250" / "1250.50"
  if (/^\d+(\.\d{1,2})?$/.test(t)) {
    return Number(t);
  }
  return null;
};

const FacturaForm: React.FC<FacturaFormProps> = ({
  onClose,
  onSuccess,
  paciente,
  mode = 'creacion',
  facturaInicial,
  seguroFactura = null,
  examenesFactura = null
}) => {
  // Estados
  const [pacienteId, setPacienteId] = useState(paciente?._id || '');
  // Factura a aseguradora: sujeto = Seguro, sin paciente ni muestras.
  const esFacturaAseguradora = seguroFactura !== null;
  const seguroFacturaId = seguroFactura?.id ?? null;
  // Clave serializada de las líneas del período: es el dep del efecto de
  // cobertura. Si el padre re-renderiza con un array nuevo pero con el mismo
  // contenido, no se vuelve a pedir la póliza (evita el loop de fetch).
  const examenesFacturaKey = JSON.stringify(examenesFactura ?? null);
  const [cargandoCobertura, setCargandoCobertura] = useState(false);
  const [errorCobertura, setErrorCobertura] = useState<string | null>(null);
  // Exámenes de la póliza, para resolver nombre/código aunque no estén en la
  // primera página del catálogo general.
  const [examenesCobertura, setExamenesCobertura] = useState<Examen[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [tipoFactura, setTipoFactura] = useState<TipoFactura>('secuencia_automatica');
  const [selectedExamenes, setSelectedExamenes] = useState<ExamenSeleccionado[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [observaciones, setObservaciones] = useState('');
  const [rnc, setRnc] = useState('');
  const [nombreEmpresa, setNombreEmpresa] = useState('');
  // Texto tecleado del precio unitario por examenId (borrador en edición).
  const [preciosBorrador, setPreciosBorrador] = useState<Record<string, string>>({});
  // Mismo borrador para la cantidad: permite dejar el campo vacío mientras se
  // escribe sin que el input controlado restaure el valor anterior.
  const [cantidadesBorrador, setCantidadesBorrador] = useState<Record<string, string>>({});

  // EEM-1 (rework): emitting e-CF needs TWO tenant decisions (fe-config):
  // the `facturacion-electronica` page (section access) AND
  // `configuracion.facturacionElectronicaActiva !== false` (emission switch in
  // Configuración → Facturación). The page alone is NOT enough: an admin can
  // stop emitting while the section stays readable, and then new invoices MUST
  // be traditional CF/CCF/GUB. `flagCargado` mirrors the hook's loading so the
  // submit button waits for the tenant resolution (fail-closed, as before).
  const { hasPageAccess, currentTenant, loading: permisosCargando } = usePagePermissions();
  const electronica =
    hasPageAccess('facturacion-electronica') &&
    currentTenant?.configuracion?.facturacionElectronicaActiva !== false;
  const flagCargado = !permisosCargando;
  // CLI-3: stored Cliente is the source of truth for e31 buyer identity.
  const [clientes, setClientes] = useState<ClienteDGII[]>([]);
  const [clienteId, setClienteId] = useState('');

  // Referencias
  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Hooks
  // Cargar todos los exámenes (el límite por defecto de 25 ocultaba el resto de la paginación)
  const { examenes } = useExamenes(1000);
  const { pacientes } = usePacientes();

  // Constantes
  // EEM-1: in electronic mode the secuencia_automatica option is NOT valid —
  // the backend's resolverTipoComprobante throws for it (F2, fail-fast). The
  // electronic mapping is consumidor_final→e32, credito_fiscal→e31,
  // gubernamental→e45 (EEM-6).
  const tiposFactura = electronica
    ? [
        { id: 'consumidor_final', label: 'Consumidor Final' },
        { id: 'credito_fiscal', label: 'Crédito Fiscal' },
        { id: 'gubernamental', label: 'Gubernamental' }
      ]
    : [
        { id: 'secuencia_automatica', label: 'Secuencia Automática' },
        { id: 'consumidor_final', label: 'Consumidor Final' },
        { id: 'credito_fiscal', label: 'Crédito Fiscal' },
        { id: 'gubernamental', label: 'Gubernamental' }
      ];

  // EEM-1/CLI-3: load the stored clientes when the tenant is electronic (the
  // electronic branch is now derived from page permissions, no flag fetch).
  // La factura a aseguradora también los necesita aun en un tenant sin
  // facturación electrónica: el comprador es la ARS y su RNC vive en el Cliente.
  useEffect(() => {
    let activo = true;
    if (!electronica && !esFacturaAseguradora) return;
    (async () => {
      try {
        // FIX 4 (JD R1): the secuencia_automatica→consumidor_final conversion
        // only applies to NEW invoices. In edicion mode this effect runs AFTER
        // the edit effect and must NOT overwrite the invoice's persisted
        // tipoFactura (e.g. credito_fiscal) with consumidor_final — a save
        // then silently rewrote the fiscal type.
        if (electronica) {
          setTipoFactura((prev) =>
            mode === 'creacion' && prev === 'secuencia_automatica' ? 'consumidor_final' : prev
          );
        }
        if (activo) setClientes(await obtenerClientesDGII());
      } catch (error) {
        // Fail-open for the LEGACY flow: an unreadable client list keeps the
        // electronic modal working (the backend still enforces its own branch).
      }
    })();
    return () => {
      activo = false;
    };
  }, [electronica, esFacturaAseguradora, mode]);

  // Effects
  useEffect(() => {
    if (mode === 'edicion' && facturaInicial) {
      setPacienteId(facturaInicial.paciente._id);
      setTipoFactura(facturaInicial.tipoFactura);
      setObservaciones(facturaInicial.observaciones || '');
      setRnc(facturaInicial.rnc || '');
      setNombreEmpresa(facturaInicial.nombreEmpresa || '');

      const examenesConvertidos = facturaInicial.examenes.map((examen: any) => ({
        examenId: examen.examen._id,
        cantidad: examen.cantidad,
        precioUnitario: examen.precioUnitario,
        // EEM-7: restore the per-item flag (default false = EXENTO).
        aplicaImpuesto: examen.aplicaImpuesto === true
      }));

      setSelectedExamenes(examenesConvertidos);
    }
  }, [mode, facturaInicial]);

  useEffect(() => {
    if (paciente) {
      setPacienteId(paciente._id);
    }
  }, [paciente]);

  // Exámenes de la póliza: se precargan con el descuento de la ARS YA aplicado
  // (el precio sigue siendo editable) y el comprobante se fija a crédito fiscal.
  useEffect(() => {
    if (!esFacturaAseguradora || !seguroFacturaId) return;
    let activo = true;
    setTipoFactura('credito_fiscal');
    setCargandoCobertura(true);
    setErrorCobertura(null);
    fetchCoberturaSeguro(seguroFacturaId)
      .then((seguro) => {
        if (!activo) return;
        const descuento = Number(seguro.descuento) || 0;
        const cubiertos = (seguro.examenesAplicables || []).filter(
          (examen) => examen && examen._id
        );
        setExamenesCobertura(cubiertos);
        const precioConDescuento = (precio: number) =>
          Math.round((Number(precio) || 0) * (1 - descuento / 100) * 100) / 100;
        // Se factura lo que la ARS consumió en el período, no la póliza entera:
        // la cantidad sale del reporte y el precio de la póliza actual.
        const lineas: { examenId: string; nombre?: string; cantidad: number }[] | null =
          examenesFacturaKey ? JSON.parse(examenesFacturaKey) : null;
        if (lineas && lineas.length > 0) {
          const cantidadPorExamen = new Map(
            lineas.map((linea) => [String(linea.examenId), linea.cantidad])
          );
          const seleccionados = cubiertos
            .filter((examen) => cantidadPorExamen.has(String(examen._id)))
            .map((examen) => ({
              examenId: examen._id,
              cantidad: Math.max(1, Math.trunc(Number(cantidadPorExamen.get(String(examen._id)))) || 1),
              precioUnitario: precioConDescuento(examen.precio)
            }));
          setSelectedExamenes(seleccionados);
          const idsPoliza = new Set(cubiertos.map((examen) => String(examen._id)));
          const fueraDePoliza = lineas.filter(
            (linea) => !idsPoliza.has(String(linea.examenId))
          );
          if (fueraDePoliza.length > 0) {
            const detalle = fueraDePoliza
              .map((linea) => linea.nombre || String(linea.examenId))
              .join(', ');
            setErrorCobertura(
              `No se precargaron ${fueraDePoliza.length} examen(es) del período porque no están en la póliza actual: ${detalle}.`
            );
          } else if (seleccionados.length === 0) {
            setErrorCobertura(
              'Los exámenes del período no están en la póliza actual de la aseguradora.'
            );
          }
        } else {
          setSelectedExamenes(
            cubiertos.map((examen) => ({
              examenId: examen._id,
              cantidad: 1,
              precioUnitario: precioConDescuento(examen.precio)
            }))
          );
          if (cubiertos.length === 0) {
            setErrorCobertura('Esta aseguradora no tiene exámenes configurados en su póliza.');
          }
        }
      })
      .catch(() => {
        if (activo) setErrorCobertura('No se pudo cargar la cobertura de la aseguradora.');
      })
      .finally(() => {
        if (activo) setCargandoCobertura(false);
      });
    return () => {
      activo = false;
    };
  }, [esFacturaAseguradora, seguroFacturaId, examenesFacturaKey]);

  // Obtener paciente seleccionado y su seguro
  const pacienteSeleccionado = pacientes.find(p => p._id === pacienteId);
  const seguroPaciente = pacienteSeleccionado?.seguro || null;

  // El catálogo general puede no incluir los exámenes de la póliza (la lista se
  // pagina): en modo aseguradora se resuelven contra la cobertura cargada.
  const examenesVisibles = esFacturaAseguradora ? examenesCobertura : examenes;

  // Handlers
  const handleExamenSelect = (examen: Examen) => {
    setSelectedExamenes(prev => {
      const existingIndex = prev.findIndex(e => e.examenId === examen._id);

      if (existingIndex >= 0) {
        const newExamenes = [...prev];
        newExamenes[existingIndex].cantidad += 1;
        return newExamenes;
      }

      return [...prev, {
        examenId: examen._id,
        cantidad: 1,
        precioUnitario: examen.precio
      }];
    });

    setSearchTerm('');
    setShowResults(false);
    // El clic en un resultado dispara blur en el input; sin este foco extra el
    // usuario pierde la barra de búsqueda después de cada examen agregado.
    searchInputRef.current?.focus();
    toast.success(`${examen.nombre} agregado`);
  };

  const handleCantidadChange = (index: number, texto: string) => {
    // El input es la única vía de edición (sin botones +/-), así que el texto
    // tecleado vive en `cantidadesBorrador` para poder borrar el campo sin que
    // el valor controlado rebote y deje el cursor al final ("155" al escribir 5).
    const item = selectedExamenes[index];
    if (!item) return;

    const limpio = texto.trim();
    setCantidadesBorrador(prev => ({ ...prev, [item.examenId]: limpio }));

    if (limpio === '') return; // vacío: no toca el estado todavía
    const numero = Number(limpio);
    if (!Number.isFinite(numero) || numero < 1) return;
    setSelectedExamenes(prev => {
      const nuevos = [...prev];
      nuevos[index] = { ...nuevos[index], cantidad: Math.trunc(numero) };
      return nuevos;
    });
  };

  // Al salir con el campo vacío o inválido se descarta el borrador y el input
  // vuelve a mostrar `item.cantidad` (que solo cambia con valores >= 1).
  const handleCantidadBlur = (index: number) => {
    const item = selectedExamenes[index];
    if (!item) return;
    setCantidadesBorrador(prev => {
      const nuevo = { ...prev };
      delete nuevo[item.examenId];
      return nuevo;
    });
  };

  // Precio editable por ítem. El texto tecleado vive en `preciosBorrador`
  // (clave = examenId) para poder escribir "12." o dejarlo vacío sin que un
  // input controlado numérico redondee/destruya el valor en cada tecla; el
  // número se sincroniza al estado y se redondea a 2 decimales al salir.
  const handlePrecioChange = (index: number, texto: string) => {
    const item = selectedExamenes[index];
    if (!item) return;

    const limpio = texto.trim();
    setPreciosBorrador(prev => ({ ...prev, [item.examenId]: limpio }));

    // Mientras se escribe se acepta un separador final ("12.") pero nada que
    // no sea interpretable como importe: así no se inventa un total.
    const numero = parsePrecio(limpio, true);
    if (numero === null || !Number.isFinite(numero) || numero < 0) return;

    setSelectedExamenes(prev => {
      const nuevos = [...prev];
      nuevos[index] = { ...nuevos[index], precioUnitario: numero };
      return nuevos;
    });
  };

  const handlePrecioBlur = (index: number) => {
    const item = selectedExamenes[index];
    if (!item) return;

    const borrador = preciosBorrador[item.examenId];
    const parseado = borrador !== undefined
      ? parsePrecio(borrador) ?? parsePrecio(borrador.replace(/[.,]$/, ''))
      : null;
    const base = parseado !== null && Number.isFinite(parseado) && parseado >= 0
      ? parseado
      : item.precioUnitario;
    const redondeado = Math.round(base * 100) / 100;

    if (redondeado !== item.precioUnitario) {
      setSelectedExamenes(prev => {
        const nuevos = [...prev];
        nuevos[index] = { ...nuevos[index], precioUnitario: redondeado };
        return nuevos;
      });
    }

    setPreciosBorrador(prev => {
      const nuevo = { ...prev };
      delete nuevo[item.examenId];
      return nuevo;
    });
  };

  const removeExamen = (index: number) => {
    const examen = examenesVisibles.find(e => e._id === selectedExamenes[index].examenId);
    setSelectedExamenes(prev => prev.filter((_, i) => i !== index));
    if (examen) {
      toast.success(`${examen.nombre} eliminado`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // La factura a aseguradora no tiene paciente: el sujeto es el Seguro, que
    // viaja como `seguroId` y el servidor resuelve contra su póliza.
    if (!esFacturaAseguradora && !pacienteId) {
      toast.error('Por favor seleccione un paciente');
      return;
    }

    if (selectedExamenes.length === 0) {
      toast.error('Por favor agregue al menos un examen');
      return;
    }

    // e31 (Crédito Fiscal) y e45 (Gubernamental) comparten la misma exigencia
    // de identidad del comprador — ambos emiten <Comprador> con RNC.
    if (tipoFactura === 'credito_fiscal' || tipoFactura === 'gubernamental') {
      const etiquetaTipo =
        tipoFactura === 'credito_fiscal' ? 'Crédito Fiscal' : 'Gubernamental';
      if (electronica || esFacturaAseguradora) {
        // CLI-3: a stored Cliente (clienteId) is the source of truth; the
        // legacy rnc/nombreEmpresa pair is only the fallback when no stored
        // Cliente is selected. At least ONE identity path must be complete.
        // La factura a aseguradora usa SIEMPRE este camino: el comprador es un
        // contribuyente con RNC y su Cliente almacenado es el registro canónico.
        const conCliente = !!clienteId;
        const conLegacy = rnc.trim() !== '' && nombreEmpresa.trim() !== '';
        if (!conCliente && !conLegacy) {
          toast.error(
            `Seleccione un Cliente o complete RNC y nombre de empresa para ${etiquetaTipo}`
          );
          return;
        }
      } else if (!rnc.trim() || !nombreEmpresa.trim()) {
        toast.error(
          `El RNC y el nombre de la empresa son requeridos para facturas de ${etiquetaTipo}`
        );
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // EEM-7: the electronic total is TAX-INCLUSIVE (gravado + ITBIS + exento)
      // with the SAME per-item rounding the backend uses (R5/R7) — the server
      // rejects a pre-tax total with "El total no coincide con la suma de los
      // subtotales".
      const desglose = desgloseElectronico();
      const total = electronica ? desglose.total : selectedExamenes.reduce((sum, item) => {
        const descuento = typeof seguroPaciente === 'object' && seguroPaciente ? seguroPaciente.descuento : 0;
        const precioConDescuento = item.precioUnitario * (1 - descuento / 100);
        return sum + item.cantidad * precioConDescuento;
      }, 0);

      const examenesPayload = electronica
        ? selectedExamenes.map((examen) => ({
            examenId: examen.examenId,
            cantidad: examen.cantidad,
            precioUnitario: examen.precioUnitario,
            // EEM-7: the flag travels per item (default false = EXENTO).
            aplicaImpuesto: examen.aplicaImpuesto === true
          }))
        : selectedExamenes;

      const facturaData = esFacturaAseguradora && seguroFacturaId
        ? {
            origen: 'aseguradora' as const,
            seguroId: seguroFacturaId,
            tipoFactura: 'credito_fiscal' as const,
            examenes: examenesPayload,
            total,
            observaciones,
            fecha: new Date(),
            estado: 'pendiente' as const,
            ...(clienteId ? { clienteId } : { rnc, nombreEmpresa })
          }
        : {
            paciente: pacienteId,
            tipoFactura,
            examenes: examenesPayload,
            total,
            observaciones,
            fecha: new Date(),
            estado: 'pendiente' as const,
            ...((tipoFactura === 'credito_fiscal' || tipoFactura === 'gubernamental') &&
              (electronica
                ? clienteId
                  ? { clienteId }
                  : { rnc, nombreEmpresa }
                : { rnc, nombreEmpresa }))
          };

      if ('numeroFactura' in facturaData) {
        delete (facturaData as any).numeroFactura;
      }

      let numeroEmision: string | null = null;

      if (mode === 'edicion' && facturaInicial) {
        const updateData = {
          ...facturaData,
          examenes: selectedExamenes.map(examen => ({
            examenId: examen.examenId,
            cantidad: examen.cantidad,
            precioUnitario: examen.precioUnitario
          }))
        };
        await updateFactura(facturaInicial._id, updateData);
      } else {
        // Emit-on-create: the server consumes a real atomic e-NCF and
        // transmits to the DGII at creation, so `numeroFactura` IS the e-NCF.
        const creada = await createFactura(facturaData);
        numeroEmision = electronica && creada?.numeroFactura ? creada.numeroFactura : null;
      }

      if (numeroEmision) {
        toast.success(`Factura electrónica creada — e-NCF ${numeroEmision}`);
      } else {
        toast.success(`Factura ${mode === 'edicion' ? 'actualizada' : 'creada'} exitosamente`);
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error(`Error al ${mode === 'edicion' ? 'actualizar' : 'crear'} factura:`, error);
      // Surface the server's real message (prevalidation / NCF range / DGII
      // gate failures) instead of a generic toast that hides the cause.
      const mensajeServidor = (error as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      toast.error(mensajeServidor || `Error al ${mode === 'edicion' ? 'actualizar' : 'crear'} la factura`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Computed values
  const filteredExamenes = examenes.filter(examen =>
    examen.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    examen.codigo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const total = selectedExamenes.reduce((sum, item) => {
    const descuento = typeof seguroPaciente === 'object' && seguroPaciente ? seguroPaciente.descuento : 0;
    const precioConDescuento = item.precioUnitario * (1 - descuento / 100);
    return sum + item.cantidad * precioConDescuento;
  }, 0);

  // EEM-7: server-side desglose mirror (facturaService.calcularDesgloseElectronico):
  // ITBIS rounded PER-ITEM at 18%, tax-inclusive total = gravado + ITBIS + exento.
  // The backend validates the sent total against this exact math.
  const desgloseElectronico = () => {
    let montoGravado = 0;
    let totalITBIS = 0;
    let montoExento = 0;
    for (const item of selectedExamenes) {
      const descuento = typeof seguroPaciente === 'object' && seguroPaciente ? seguroPaciente.descuento : 0;
      const subtotal = item.precioUnitario * (1 - descuento / 100) * item.cantidad;
      if (item.aplicaImpuesto) {
        montoGravado += subtotal;
        totalITBIS += Math.round(subtotal * 0.18 * 100) / 100;
      } else {
        montoExento += subtotal;
      }
    }
    return { montoGravado, totalITBIS, montoExento, total: montoGravado + totalITBIS + montoExento };
  };

  // U4/G3: an electronic invoice (e-NCF issued OR ITBIS desglose present —
  // provisional) cannot be edited through this legacy flow; the backend
  // rejects it with a 400. The modal shows the notice and disables Save.
  const esFacturaElectronica =
    mode === 'edicion' &&
    !!facturaInicial &&
    (!!facturaInicial.encf ||
      (facturaInicial.montoGravado || 0) > 0 ||
      (facturaInicial.totalITBIS || 0) > 0 ||
      (facturaInicial.montoExento || 0) > 0);

  const desglose = desgloseElectronico();

  return (
    <div className="flex h-[90vh] max-h-[95vh] w-full max-w-7xl mx-auto bg-white rounded-lg shadow-lg border overflow-hidden">
      {/* Header ultra compacto */}
      <div className="flex flex-col w-full">
        <div className="flex justify-between items-center px-4 py-2 bg-blue-600 text-white">
          <h2 className="text-base font-medium">
            {esFacturaAseguradora
              ? `Factura a ${seguroFactura?.nombre}`
              : mode === 'edicion'
                ? 'Editar Factura'
                : 'Nueva Factura'}
          </h2>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white p-1 rounded transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Contenido en dos columnas */}
        <div className="flex flex-1 overflow-hidden">
          {/* Columna izquierda - Formulario compacto */}
          <div className="w-1/2 overflow-y-auto p-3 space-y-3 border-r">
            <form onSubmit={handleSubmit} className="space-y-3">
              {/* Tipo de factura y sujeto: paciente o aseguradora */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Factura
                  </label>
                  <select
                    value={tipoFactura}
                    onChange={(e) => setTipoFactura(e.target.value as TipoFactura)}
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    disabled={mode === 'edicion' || esFacturaAseguradora}
                  >
                    {tiposFactura.map(tipo => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  {esFacturaAseguradora ? (
                    <div className="h-full rounded border border-blue-200 bg-blue-50 p-2 text-xs text-blue-800">
                      <p className="font-medium">Factura a {seguroFactura?.nombre}</p>
                      <p className="mt-1 text-[11px] text-blue-700">
                        No requiere paciente ni genera muestras. Los exámenes de la póliza se
                        cargan automáticamente con el descuento del seguro aplicado.
                      </p>
                    </div>
                  ) : (
                    <>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Paciente
                      </label>
                      <select
                        value={pacienteId}
                        onChange={(e) => setPacienteId(e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        required
                        disabled={mode === 'edicion'}
                      >
                        <option value="">Seleccione un paciente</option>
                        {pacientes.map(paciente => (
                          <option key={paciente._id} value={paciente._id}>
                            {paciente.nombre} - {paciente.codigo}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                </div>
              </div>

              {/* EEM-1 banner: the tenant operates in electronic mode */}
              {electronica && mode === 'creacion' && (
                <div className="bg-purple-50 p-2 rounded border border-purple-200 flex items-start space-x-2">
                  <Zap className="h-4 w-4 text-purple-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-purple-800">
                    Facturación electrónica activa: la factura nace PROVISIONAL (P-…) y el e-NCF se
                    emite al pagar. ITBIS por ítem (18%) — los ítems sin marcar se registran EXENTOS.
                  </p>
                </div>
              )}

              {/* Información fiscal: CLI-3 (Cliente almacenado) o fallback legacy */}
              {(tipoFactura === 'credito_fiscal' || tipoFactura === 'gubernamental') && (
                <div className="bg-orange-50 p-2 rounded border border-orange-200">
                  <h3 className="text-xs font-medium text-orange-800 mb-1">
                    Información Fiscal
                  </h3>
                  {(electronica || esFacturaAseguradora) && clientes.length > 0 && (
                    <div className="space-y-1">
                      <select
                        value={clienteId}
                        onChange={(e) => {
                          const id = e.target.value;
                          if (id !== '') {
                            const cliente = clientes.find((c) => c._id === id);
                            // CLI-2: block a stored client whose RNC fails the
                            // DGII checksum — the emission prevalidar (R2-6)
                            // would fail-closed later, so surface it now.
                            if (cliente && cliente.rnc && !validarRNC(cliente.rnc)) {
                              toast.error(
                                `Client "${cliente.nombre}" has an invalid RNC — it cannot be used on this invoice`
                              );
                              return;
                            }
                          }
                          setClienteId(id);
                        }}
                        className="w-full border border-orange-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-500 text-xs"
                      >
                        <option value="">Seleccione un Cliente almacenado…</option>
                        {clientes.map((cliente) => (
                          <option key={cliente._id} value={cliente._id}>
                            {cliente.nombre}
                            {cliente.rnc ? ` — ${cliente.rnc}` : ''}
                          </option>
                        ))}
                      </select>
                      <p className="text-[10px] text-orange-700">
                        El Cliente almacenado es la fuente de verdad (CLI-3). Sin selección, use el
                        fallback RNC/nombre de empresa.
                      </p>
                    </div>
                  )}
                  {/* CLI-3 fallback (JD R1): always reachable while no stored
                      Cliente is selected. The previous render swapped the inputs
                      out entirely when clientes existed — a user without a
                      matching stored Cliente could never invoice an e31 (the
                      select forced a clienteId and the inputs were unreachable).
                      The stored Cliente wins once selected; until then the legacy
                      inputs are the only way to satisfy the buyer requirement. */}
                  {clienteId === '' && (
                    <div className="grid grid-cols-2 gap-1">
                      <input
                        type="text"
                        value={rnc}
                        onChange={(e) => setRnc(e.target.value)}
                        className="w-full border border-orange-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-500 text-xs"
                        placeholder="RNC"
                        required
                      />
                      <input
                        type="text"
                        value={nombreEmpresa}
                        onChange={(e) => setNombreEmpresa(e.target.value)}
                        className="w-full border border-orange-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-500 text-xs"
                        placeholder="Nombre de la empresa"
                        required
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Búsqueda de exámenes: en una factura a aseguradora el
                  catálogo está acotado a la póliza, así que no hay buscador. */}
              {esFacturaAseguradora ? (
                <div className="rounded border border-blue-200 bg-blue-50 p-2 text-xs text-blue-800">
                  {cargandoCobertura ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Cargando exámenes de la póliza…
                    </span>
                  ) : (
                    <>
                      <p className="font-medium">Exámenes de la póliza</p>
                      <p className="mt-0.5 text-[11px] text-blue-700">
                        {selectedExamenes.length} exámenes precargados con el descuento del seguro.
                        Podés ajustar precio y cantidad, o quitar los que no correspondan.
                      </p>
                    </>
                  )}
                  {errorCobertura && (
                    <p className="mt-1 text-[11px] font-medium text-red-700">{errorCobertura}</p>
                  )}
                </div>
              ) : (
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Buscar Exámenes
                </label>
                <div className="relative">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowResults(true);
                    }}
                    className="w-full border border-gray-300 rounded pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Buscar por nombre o código..."
                  />
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                </div>

                {showResults && searchTerm && (
                  <div
                    ref={resultsRef}
                    className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-80 overflow-y-auto"
                  >
                    {filteredExamenes.map(examen => (
                      <div
                        key={examen._id}
                        // prevenir el blur del input al hacer mousedown: el foco
                        // se conserva en la barra al seleccionar un resultado.
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleExamenSelect(examen)}
                        className="px-2 py-1.5 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-medium text-gray-800 truncate">{examen.nombre}</span>
                          <span className="text-xs text-blue-600 font-semibold">
                            {formatCurrency(examen.precio)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {examen.codigo}
                        </div>
                      </div>
                    ))}
                    {filteredExamenes.length === 0 && (
                      <div className="px-2 py-3 text-gray-500 text-center text-xs">
                        No se encontraron resultados
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}

              {/* Observaciones compactas */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Observaciones
                </label>
                <textarea
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none text-xs"
                  rows={2}
                  placeholder="Observaciones adicionales..."
                />
              </div>
            </form>
          </div>

          {/* Columna derecha - Exámenes seleccionados */}
          <div className="flex flex-col w-1/2 h-full">
            <div className="p-2 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center">
                <span className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2">
                  {selectedExamenes.length}
                </span>
                Exámenes Seleccionados
              </h3>
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {selectedExamenes.length === 0 ? (
                <div className="text-center text-gray-500 mt-4">
                  <div className="text-2xl mb-1">{esFacturaAseguradora ? '🏥' : '📝'}</div>
                  <p className="text-xs">
                    {cargandoCobertura
                      ? 'Cargando la cobertura…'
                      : esFacturaAseguradora
                        ? 'La aseguradora no tiene exámenes configurados'
                        : 'No hay exámenes seleccionados'}
                  </p>
                  {!esFacturaAseguradora && (
                    <p className="text-xs text-gray-400 mt-1">Busca exámenes desde la izquierda</p>
                  )}
                </div>
              ) : (
                <div className="space-y-1">
                  {selectedExamenes.map((item, index) => {
                    const examen = examenesVisibles.find(e => e._id === item.examenId);
                    const descuento = typeof seguroPaciente === 'object' && seguroPaciente ? seguroPaciente.descuento : 0;

                    return (
                      <div
                        key={index}
                        className="bg-white border border-gray-200 rounded p-2 text-xs"
                      >
                        <div className="flex justify-between items-start mb-1">
                          <h4 className="font-medium text-gray-800 text-xs flex-1 mr-1 leading-tight">{examen?.nombre}</h4>
                          <button
                            type="button"
                            onClick={() => removeExamen(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>

                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1 text-xs text-gray-600 min-w-0 flex-1">
                            <span className="text-gray-400 shrink-0">$</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              aria-label={`Precio unitario de ${examen?.nombre || ''}`}
                              value={preciosBorrador[item.examenId] ?? String(item.precioUnitario)}
                              onChange={(e) => handlePrecioChange(index, e.target.value)}
                              onBlur={() => handlePrecioBlur(index)}
                              className="w-20 border border-gray-300 rounded px-1 py-0.5 text-right text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            <span className="text-gray-400">×</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              aria-label={`Cantidad de ${examen?.nombre || ''}`}
                              value={cantidadesBorrador[item.examenId] ?? String(item.cantidad)}
                              onChange={(e) => handleCantidadChange(index, e.target.value)}
                              onBlur={() => handleCantidadBlur(index)}
                              className="w-12 border border-gray-300 rounded px-1 py-0.5 text-center text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            {descuento > 0 && (
                              <span className="text-green-600 font-medium shrink-0">
                                -{descuento}%
                              </span>
                            )}
                          </div>
                        </div>

                        {/* EEM-7: per-item ITBIS flag (default OFF = EXENTO) */}
                        {electronica && (
                          <label className="mt-1.5 flex items-center justify-between bg-gray-50 rounded px-2 py-1 cursor-pointer">
                            <span className="text-[10px] text-gray-600">Gravado ITBIS 18%</span>
                            <input
                              type="checkbox"
                              checked={item.aplicaImpuesto === true}
                              onChange={(e) => {
                                setSelectedExamenes((prev) => {
                                  const nuevo = [...prev];
                                  nuevo[index] = {
                                    ...nuevo[index],
                                    aplicaImpuesto: e.target.checked
                                  };
                                  return nuevo;
                                });
                              }}
                              className="rounded"
                            />
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer compacto */}
        <div className="border-t border-gray-200 p-3 bg-gray-50">
          {esFacturaElectronica && (
            <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
              Esta factura es electrónica (e-CF): no se edita por este flujo — use el flujo de
              emisión electrónica (anulación / nota de crédito).
            </div>
          )}

          {/* EEM-7: ITBIS desglose — taxes included in the total */}
          {electronica && mode === 'creacion' && (
            <div className="flex justify-between items-center mb-1 text-xs text-gray-600">
              <div className="flex space-x-4">
                <span>Gravado: <b className="text-gray-800">{formatCurrency(desglose.montoGravado)}</b></span>
                <span>ITBIS: <b className="text-gray-800">{formatCurrency(desglose.totalITBIS)}</b></span>
                <span>Exento: <b className="text-gray-800">{formatCurrency(desglose.montoExento)}</b></span>
              </div>
            </div>
          )}

          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Total:</span>
            <span className="text-lg font-bold text-blue-600">
              {formatCurrency(electronica && mode === 'creacion' ? desglose.total : total)}
            </span>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 border border-gray-300 rounded text-gray-700 hover:bg-gray-100 transition-colors text-sm"
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 transition-colors text-sm"
              disabled={
                (esFacturaAseguradora ? !seguroFacturaId : !pacienteId) ||
                selectedExamenes.length === 0 ||
                isSubmitting ||
                esFacturaElectronica ||
                cargandoCobertura ||
                !flagCargado
              }
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>Procesando...</span>
                </>
              ) : (
                <span>{mode === 'edicion' ? 'Actualizar' : 'Crear Factura'}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacturaForm;
